'use client';
import React from 'react';
import { SessionContext } from '@benzinga/session-context';
import { QuotesManager, Security, GetQuotesLogoParams, Logo } from '@benzinga/quotes-manager';

export const useLogo = (symbols: Security[], params?: GetQuotesLogoParams, callback?: (logos: Logo[]) => void) => {
  const [logos, setLogos] = React.useState<Logo[]>([]);
  useLogoCallback(symbols, params, callback ? callback : setLogos);
  return logos;
};

const useLogoCallback = (symbols: Security[], params?: GetQuotesLogoParams, callback?: (logos: Logo[]) => void) => {
  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(QuotesManager);
  const symbolsRef = React.useRef<string[]>([]);
  const limit = 100;

  const getCachedLogos = React.useCallback(() => {
    const logosCached = symbols.map(s => quotesManager.getQuotesLogoCached(s, params));
    return logosCached.filter(logo => logo !== undefined) as Logo[];
  }, [params, quotesManager, symbols]);

  const fetchLogosBatch = React.useCallback(
    async (startIndex: number, endIndex: number) => {
      const batchSymbols = symbols.slice(startIndex, endIndex);
      const cachedLogos = batchSymbols.map(s => quotesManager.getQuotesLogoCached(s, params));
      const hasAllCached = cachedLogos.every(logo => logo !== undefined);

      if (hasAllCached) {
        return cachedLogos as Logo[];
      }

      const logos = await quotesManager.getQuotesLogos(batchSymbols, params);
      if (logos.err) {
        return [];
      }
      return logos.ok;
    },
    [params, quotesManager, symbols],
  );

  const fetchAllLogos = React.useCallback(async () => {
    const cachedLogos = getCachedLogos();
    const hasAllSymbols = symbols.every(s => cachedLogos.some(logo => logo.search_key === s));

    if (hasAllSymbols) {
      if (callback) {
        callback(cachedLogos);
      }
      return cachedLogos;
    }

    const allLogos: Logo[] = [];
    for (let i = 0; i < symbols.length; i += limit) {
      const batchLogos = await fetchLogosBatch(i, i + limit);
      allLogos.push(...(batchLogos as Logo[]));
    }

    if (callback) {
      callback(allLogos);
    }
    return allLogos;
  }, [getCachedLogos, fetchLogosBatch, symbols, callback]);

  React.useEffect(() => {
    const currentSymbols = symbols.map(s => s.toString());
    const previousSymbols = symbolsRef.current;

    if (JSON.stringify(currentSymbols) !== JSON.stringify(previousSymbols)) {
      fetchAllLogos();
      symbolsRef.current = currentSymbols;
    }
  }, [symbols, fetchAllLogos]);
};
